import React, { useEffect, useState, useRef, useCallback } from 'react';
import {
  Icon,
  Link,
  SiteHeaderV2,
  SiteHeaderV2ItemGroup,
  Text,
  UniversalHeader,
  UniversalHeaderItem,
  VerticalSpace,
} from '@arc-ui/components';
import type {
  NavigationData,
  LinkItem,
  LinkGroup,
  EEBHeaderDesktopProps,
} from '../../../types/ee-header';
import { useHeaderContext } from '../../../contexts';
import { CHEVRON_RIGHT_ICON } from '../../atoms/SVG';

const renderLinkListItem = ([itemKey, item]: [string, LinkItem]) => (
  <li key={itemKey} data-href={item.linkUrl} data-testid="site-header-item">
    <a className="bt-hover-anchor" href={item.linkUrl}>
      {item.title}
    </a>
  </li>
);

const renderItemGroup = ([linkKey, linkData]: [
  string,
  LinkGroup | string | undefined,
]) => {
  if (typeof linkData === 'string' || !linkData) return null;
  return (
    <SiteHeaderV2ItemGroup key={linkKey} href={linkData.mainLink}>
      <Link
        key={linkKey}
        isImplied
        size="m"
        title={linkData.title}
        href={linkData.mainLink}
      >
        {linkData.title}
      </Link>
      <VerticalSpace size="12" />
      {linkData.linkListItems &&
        Object.entries(linkData.linkListItems).map(renderLinkListItem)}
    </SiteHeaderV2ItemGroup>
  );
};

const EEBHeaderDesktop: React.FC<EEBHeaderDesktopProps> = ({
  overlayOpen,
  searchTriggerEl,
  basketEl,
  utilityLinksEl,
}) => {
  const {
    businessSegmentTitle,
    businessSegmentLinkUrl,
    personalSegmentTitle,
    personalSegmentLinkUrl,
    nav,
    logInLinkUrl,
    logInLinkTitle,
  } = useHeaderContext();

  const [activeNav, setActiveNav] = useState<string>('');
  const [navData, setNavData] = useState<
    Record<string, { navData: NavigationData; columnKeys: string[] }>
  >({});
  const [activeNavData, setActiveNavData] = useState<
    { navData: NavigationData; columnKeys: string[] } | undefined
  >(undefined);

  const modalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClick(event: MouseEvent) {
      if (
        modalRef.current &&
        !modalRef.current.contains(event.target as Node)
      ) {
        const navArr = Object.keys(navData);
        const target = event.target as HTMLElement;
        if (!['link-url-box', 'bt-hover-anchor'].includes(target.className)) {
          if (!navArr.includes(target.textContent ?? '')) {
            setActiveNav('');
            document.removeEventListener('mousedown', handleClick);
          }
        }
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => {
      document.removeEventListener('mousedown', handleClick);
    };
  }, [activeNav, navData]);

  useEffect(() => {
    if (nav) {
      const navInfo: Record<
        string,
        { navData: NavigationData; columnKeys: string[] }
      > = {};
      Object.entries(nav).forEach(([key, value]) => {
        const nested: NavigationData =
          value?.hover_navigation_cop || value?.hover_navigation || {};
        const columnKeys = Object.keys(nested)
          .filter(
            (k): k is `column${number}` =>
              k.startsWith('column') &&
              Boolean(nested[k as `column${number}`]) &&
              typeof nested[k as `column${number}`] === 'object' &&
              nested[k as `column${number}`] !== null &&
              Object.keys(
                nested[k as `column${number}`] as Record<string, unknown>,
              ).length > 0,
          )
          .sort((a, b) => a.localeCompare(b));
        navInfo[key] = { navData: nested, columnKeys };
        setNavData(navInfo);
      });
    }
  }, [nav]);

  useEffect(() => {
    setActiveNavData(navData[activeNav]);
  }, [activeNav, navData]);

  const capitalizeIfNeeded = useCallback((sentence: string): string => {
    return sentence === sentence?.toUpperCase()
      ? sentence
      : sentence?.toUpperCase();
  }, []);

  return (
    <>
      <UniversalHeader ContentSlot={utilityLinksEl}>
        <UniversalHeaderItem href={personalSegmentLinkUrl || '#'}>
          <Text size="m" id={personalSegmentTitle}>
            {personalSegmentTitle}
          </Text>
        </UniversalHeaderItem>
        <UniversalHeaderItem href={businessSegmentLinkUrl || '#'} isActive>
          <Text size="m" id={businessSegmentTitle}>
            {businessSegmentTitle}
          </Text>
        </UniversalHeaderItem>
      </UniversalHeader>
      <div
        className="nav-items"
        style={{ position: 'relative' }}
        aria-hidden={overlayOpen ? 'true' : 'false'}
      >
        <SiteHeaderV2
          subBrand="ee"
          isTransparent
          loginHref={logInLinkUrl}
          loginTitle={logInLinkTitle}
          search={searchTriggerEl}
          basket={basketEl}
        >
          {nav &&
            navData &&
            Object.keys(navData).map((key: string) => (
              <div key={key} title={key} style={{ position: 'relative' }}>
                <button
                  type="button"
                  onClick={() =>
                    setActiveNav((prev) => (prev === key ? '' : key))
                  }
                  style={{
                    background: 'none',
                    border: 'none',
                    padding: 0,
                    cursor: 'pointer',
                  }}
                >
                  <p
                    className="nav-item-p"
                    style={{ color: key === activeNav ? '#057382' : '#000' }}
                  >
                    {key}
                  </p>
                </button>
              </div>
            ))}
          {activeNav !== '' && (
            <>
              {/* <div className="panel-overlay"></div> */}
              <div
                style={{
                  position: 'absolute',
                  backgroundColor: '#fff',
                  zIndex: 1000,
                  left: '4vw',
                  right: '4vw',
                  top: 72,
                  padding: '2em',
                  boxShadow: '0 100px 100px rgba(0,0,0,0.5)',
                }}
              >
                <div ref={modalRef} className="header-nav-panel">
                  {activeNavData?.columnKeys.map((columnKey: string) => (
                    <div key={columnKey} data-testid="site-header-column">
                      {Object.entries(
                        activeNavData?.navData[
                          columnKey as `column${number}`
                        ] ?? {},
                      )
                        .filter(
                          ([linkKey]) =>
                            !['equalHeightsColumn', 'containerTheme'].includes(
                              linkKey,
                            ),
                        )
                        .map(renderItemGroup)}
                    </div>
                  ))}
                </div>
                <div
                  className="link-url-box"
                  data-href={activeNavData?.navData?.linkUrl}
                >
                  <a
                    className="bt-hover-anchor"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      fontWeight: 'bold',
                      height: '100%',
                      paddingLeft: '10px',
                    }}
                    href={activeNavData?.navData?.linkUrl}
                  >
                    {capitalizeIfNeeded(activeNavData?.navData?.title ?? '')}
                    <Icon color="auto" icon={CHEVRON_RIGHT_ICON} size={16} />
                  </a>
                </div>
              </div>
            </>
          )}
        </SiteHeaderV2>
      </div>
    </>
  );
};

export default EEBHeaderDesktop;
